"use client";
import React from "react";
import Link from "next/link";
import { Linkedin, Instagram, Youtube, ExternalLink } from "lucide-react";
import { useModal } from "../Modals/ModalContext";
import { useAuthModal } from "../../hooks/useAuthModal";
import { IDX_LAUNCHLAB_CREATE_PATH } from "@/config/idxLaunchlabAppConfig";
import { IDX_EXCHANGE_URL } from "@/config/idxExchangeAppConfig";
import { IDX_TELEGRAM_BOT_URL, IDX_TELEGRAM_OFFICIAL_URL } from "@/config/idxSocialLinks";
import { SMART_SELL_NAV_ENABLED } from "@/config/smartToolsLandingConfig";
/* ---------------- ICONS ---------------- */

const DiscordIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128c.125-.094.25-.192.372-.292a.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03z" />
  </svg>
);

const TelegramIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M9.993 15.674l-.396 4.3c.568 0 .814-.244 1.11-.537l2.664-2.546 5.52 4.035c1.012.558 1.73.264 1.99-.937l3.607-16.91c.31-1.438-.52-2.003-1.507-1.64L1.36 9.41c-1.402.546-1.38 1.33-.239 1.683l5.91 1.844L19.8 5.917c.603-.399 1.152-.178.7.221" />
  </svg>
);

const XBrandIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.244 2H21.5l-7.52 8.583L23 22h-6.59l-5.155-6.103L5.256 22H2l8.058-9.19L2 2h6.59l4.833 5.717L18.244 2z" />
  </svg>
);

const allTools = [
  { name: "IDX Exchange", href: IDX_EXCHANGE_URL, external: true },
  { name: "IDX Launchlab", href: "/idx-launchlab" },
  { name: "IDX bot", href: "/idx-volume-bot" },
  { name: "Holders Maker", href: "/holders-maker" },
  { name: "Reaction Booster", href: "/reaction-booster" },
  ...(SMART_SELL_NAV_ENABLED
    ? [
        { name: "Smart Sell", href: "/smart-sell" },
        { name: "Smart Sell guide", href: "/smart-sell/learn-more" },
      ]
    : []),
  { name: "Pumpfun Bot", href: "/pumpfun-volume-bot" },
  { name: "Raydium Bot", href: "/raydium-volume-bot" },
  { name: "Orca Bot", href: "/orca-volume-bot" },
  { name: "Meteora Bot", href: "/meteora-volume-bot" },
  { name: "Token Burn", href: "/token-burn" },
  { name: "Token Hub", href: "/token-hub" },
  { name: "Fast Swap", href: "/fast-swap" },
  { name: "Community Signal", href: "/community" },
  { name: "Bulk Sender", href: "/bulk-sender/solana" },
  { name: "Update Token Metadata", href: "/update-metadata" },
  { name: "Create Liquidity Pool", href: "/liquidity-pool-creator" },
  { name: "Liquidity Manager", href: "/manage-liquidity/add-liquidity" },
  { name: "Revoke Mint Authority", href: "/revoke-mint-authority" },
  { name: "Revoke Freeze Authority", href: "/revoke-freeze" },
];

function FooterBrandBlock() {
  return (
    <>
      <div className="flex items-center gap-3">
        <div className="relative flex shrink-0 items-center">
          <img
            src="/pools/idx.png"
            alt="IDX Logo black Background"
            width={40}
            height={40}
            decoding="async"
            className="h-8 w-8 object-contain select-none sm:h-10 sm:w-10"
          />
        </div>
        <div>
          <h2 className="text-lg font-bold leading-tight text-white">IDX AutoBot</h2>
          <p className="text-xs text-gray-400">AI Web3 Engine</p>
        </div>
      </div>

      <p className="text-sm leading-relaxed text-gray-400">
        Solana tools to create tokens, manage liquidity, boost volume, and automate Web3 growth.
      </p>

      <div className="flex flex-col items-start gap-2 text-sm">
        <a
          href={IDX_TELEGRAM_BOT_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-600/45 bg-emerald-600/10 px-3 py-1.5 font-medium text-emerald-400 transition-colors hover:border-emerald-500/60 hover:bg-emerald-600/15 hover:text-emerald-300"
        >
          <TelegramIcon className="h-4 w-4 shrink-0" />
          Telegram Bot
        </a>
        <Link href={IDX_LAUNCHLAB_CREATE_PATH} className="text-gray-400 transition-colors hover:text-emerald-400" rel="noopener noreferrer">
          Create on Launchlab
        </Link>
        <Link href="/volume-bot" className="text-gray-400 transition-colors hover:text-emerald-400" rel="noopener noreferrer">
          Boost Volume
        </Link>
      </div>
    </>
  );
}

function FooterQuickLinksBlock({ onReferUsClick }) {
  return (
    <div>
      <h3 className="mb-3 text-sm font-semibold text-white">Quick Links</h3>
      <ul className="space-y-3 text-sm">
        <li>
          <Link href="/about" className="text-gray-400 transition-colors hover:text-emerald-400" rel="noopener noreferrer">
            About Us
          </Link>
        </li>
        <li>
          <a
            href="https://www.idxsolana.io"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-gray-400 transition-colors hover:text-emerald-400"
          >
            Main Website <ExternalLink className="h-3.5 w-3.5 shrink-0" />
          </a>
        </li>
        <li>
          <a
            href="https://docs.idxsolana.io"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-gray-400 transition-colors hover:text-emerald-400"
          >
            Documentation <ExternalLink className="h-3.5 w-3.5 shrink-0" />
          </a>
        </li>
        <li>
          <a
            href="https://idxwallet.net"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-gray-400 transition-colors hover:text-emerald-400"
          >
            IDX Wallet <ExternalLink className="h-3.5 w-3.5 shrink-0" />
          </a>
        </li>
        <li>
          <button
            type="button"
            onClick={onReferUsClick}
            className="text-left text-gray-400 transition-colors hover:text-emerald-400"
          >
            Refer Us
          </button>
        </li>
        <li>
          <Link
            href="/liquidity-pool-creator/liquidity-simulator"
            className="text-gray-400 transition-colors hover:text-emerald-400"
            rel="noopener noreferrer"
          >
            Liquidity Simulator
          </Link>
        </li>
      </ul>
    </div>
  );
}

function FooterTelegramBlock() {
  return (
    <div>
      <h3 className="mb-3 text-sm font-semibold text-white">Telegram Channels</h3>
      <ul className="space-y-3 text-sm">
        <li>
          <a
            href="https://t.me/IDX_Solana"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-gray-400 transition-colors hover:text-emerald-400"
          >
            Solana New Token <ExternalLink className="h-3.5 w-3.5 shrink-0" />
          </a>
        </li>
        <li>
          <a
            href="https://t.me/IDX_Crypto_News"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-gray-400 transition-colors hover:text-emerald-400"
          >
            Live Crypto News <ExternalLink className="h-3.5 w-3.5 shrink-0" />
          </a>
        </li>
      </ul>
    </div>
  );
}

function FooterToolLink({ link }) {
  const className = "text-gray-400 transition-colors hover:text-emerald-400";
  if (link.external) {
    return (
      <a
        href={link.href}
        target="_blank"
        rel="noopener noreferrer"
        className={className}
      >
        {link.name}
      </a>
    );
  }
  return (
    <Link href={link.href} className={className} rel="noopener noreferrer">
      {link.name}
    </Link>
  );
}

function FooterToolsBlock() {
  return (
    <div className="md:col-span-2 md:col-start-2 md:row-start-1">
      <h3 className="mb-3 text-sm font-semibold text-white md:mb-4">Tools</h3>
      <ul className="space-y-3 text-sm md:grid md:grid-cols-2 md:gap-x-6 md:gap-y-3 md:space-y-0">
        {allTools.map((link) => (
          <li key={link.href}>
            <FooterToolLink link={link} />
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ---------------- COMPONENT ---------------- */

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const { openReferralModal } = useModal();
  const { isAuthenticated, openLoginModal } = useAuthModal();

  const handleReferUsClick = () => {
    if (isAuthenticated) openReferralModal();
    else openLoginModal();
  };

  const socialBaseClass =
    "group p-2.5 bg-gray-800/80 backdrop-blur rounded-xl text-gray-400 hover:bg-gray-700 transition-[colors,transform] duration-300 hover:scale-110 ";

  const socialLinks = [
    {
      name: "X",
      icon: XBrandIcon,
      url: "https://x.com/IDX_SOLANA",
      hoverClass: "hover:text-white hover:shadow-white/20",
    },
    {
      name: "Discord",
      icon: DiscordIcon,
      url: "https://discord.com/invite/4mw4GBxj23",
      hoverClass: "hover:text-[#5865F2] hover:shadow-[#5865F2]/30",
    },
    {
      name: "Telegram",
      icon: TelegramIcon,
      url: IDX_TELEGRAM_OFFICIAL_URL,
      hoverClass: "hover:text-[#229ED9] hover:shadow-sky-500/25",
    },
    {
      name: "LinkedIn",
      icon: Linkedin,
      url: "https://www.linkedin.com/company/idx-solana/",
      hoverClass: "hover:text-[#0A66C2] hover:shadow-[#0A66C2]/25",
    },
    {
      name: "Instagram",
      icon: Instagram,
      url: "https://www.instagram.com/idx_solana/",
      hoverClass: "hover:text-[#E4405F] hover:shadow-[#E4405F]/25",
    },
    {
      name: "YouTube",
      icon: Youtube,
      url: "https://www.youtube.com/@idx-solana",
      hoverClass: "hover:text-[#FF0000] hover:shadow-red-600/30",
    },
  ];

  return (
    <footer className="mt-auto border-t border-gray-700/50 bg-gray-900/50 backdrop-blur-xl">
      <div className="mx-auto max-w-6xl px-5 py-10 md:px-10 2xl:px-0">
        {/*
          Content renders exactly once; the three layouts are pure CSS placement.
          Mobile: single column (Brand → Tools → Quick → Telegram).
          Tablet: Quick | Tools (2 cols) | Telegram, Brand on row 2.
          Desktop: Brand | Tools (2 cols) | Quick over Telegram.
        */}
        <div className="grid grid-cols-1 gap-8 md:grid-cols-4 md:gap-x-6 md:gap-y-10 xl:gap-10">
          <div className="flex flex-col gap-5 md:col-span-2 md:col-start-1 md:row-start-2 xl:col-span-1 xl:row-start-1">
            <FooterBrandBlock />
          </div>

          <FooterToolsBlock />

          {/* `md:contents` lets these two become grid items on tablet, then re-stack at xl. */}
          <div className="flex flex-col gap-7 md:contents xl:flex xl:col-start-4 xl:row-start-1">
            <div className="md:col-start-1 md:row-start-1">
              <FooterQuickLinksBlock onReferUsClick={handleReferUsClick} />
            </div>
            <div className="md:col-start-4 md:row-start-1">
              <FooterTelegramBlock />
            </div>
          </div>
        </div>

        {/* ── Bottom bar ── */}
        <div className="mt-12 border-t border-emerald-500/10 pt-8">
          <div className="flex flex-col gap-8 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex w-full justify-center sm:w-auto sm:justify-start">
              <div className="flex flex-wrap justify-center gap-3 sm:justify-start">
                {socialLinks.map((s) => (
                  <a
                    key={s.name}
                    href={s.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={s.name}
                    className={`${socialBaseClass} ${s.hoverClass}`}
                  >
                    <s.icon className="h-4 w-4 transition-transform group-hover:rotate-6" />
                  </a>
                ))}
              </div>
            </div>

            <div className="text-center text-sm text-gray-500 sm:text-left">
              © <span suppressHydrationWarning>{currentYear}</span>{" "}
              <span className="font-medium text-white">IDX AutoBot</span>. All rights reserved.
            </div>

            <div className="flex flex-wrap justify-center gap-5 text-sm sm:justify-end">
              <Link
                href="/privacy-policy"
                className="group relative text-gray-400 transition-colors hover:text-emerald-400"
                rel="noopener noreferrer"
              >
                Privacy
                <span className="absolute -bottom-1 left-0 h-[1px] w-full origin-left scale-x-0 bg-emerald-400 transition-transform group-hover:scale-x-100" />
              </Link>

              <Link
                href="/terms-of-service"
                className="group relative text-gray-400 transition-colors hover:text-emerald-400"
                rel="noopener noreferrer"
              >
                Terms
                <span className="absolute -bottom-1 left-0 h-[1px] w-full origin-left scale-x-0 bg-emerald-400 transition-transform group-hover:scale-x-100" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
